import React, { ElementType, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { AddIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  addDays,
  addMonths,
  addWeeks,
  format,
  getDay,
  isSameDay,
  parse,
  startOfDay,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { CalendarDays, ListFilter } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { CalendarEvent } from '../../utils/types';
import { getEmployeeDataMe, getEmployees } from '../../hooks/employees';
import {
  calendarAccent,
  CalendarCommandBar,
  CalendarEmptyState,
  CalendarEventListItem,
  CalendarHero,
  CalendarPrimaryButton,
  CalendarSectionCard,
  CalendarSecondaryButton,
  CalendarShell,
  CalendarSkeleton,
  CalendarStatusBadge,
  CalendarTypeBadge,
  formatCalendarDate,
  formatCalendarDateTime,
  getCalendarEventTypeMeta,
} from './components/calendarUi';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

type CalendarView = 'month' | 'week' | 'day' | 'agenda';

const getRangeLabel = (date: Date, view: CalendarView) => {
  if (view === 'day') {
    return format(date, 'EEEE, MMM d');
  }
  if (view === 'week') {
    return `${format(startOfWeek(date, { weekStartsOn: 1 }), 'MMM d')} - ${format(addDays(startOfWeek(date, { weekStartsOn: 1 }), 6), 'MMM d, yyyy')}`;
  }
  if (view === 'agenda') {
    return `Agenda from ${format(date, 'MMM d, yyyy')}`;
  }
  return format(date, 'MMMM yyyy');
};

const navigateDate = (currentDate: Date, view: CalendarView, direction: 'prev' | 'next') => {
  if (view === 'day') {
    return direction === 'next' ? addDays(currentDate, 1) : subDays(currentDate, 1);
  }
  if (view === 'week' || view === 'agenda') {
    return direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1);
  }
  return direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1);
};

const CalendarEventContent = ({ event }: { event: { resource: CalendarEvent; title: string } }) => {
  const meta = getCalendarEventTypeMeta(event.resource.event_type);
  return (
    <HStack spacing={1.5} align="center" overflow="hidden">
      <Box boxSize="6px" borderRadius="full" bg={meta.tint} flexShrink={0} />
      <Text fontSize="xs" fontWeight="700" noOfLines={1}>
        {event.title}
      </Text>
    </HStack>
  );
};

export const CalendarPage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const compactView = useBreakpointValue({ base: true, lg: false });
  const defaultView = useBreakpointValue<CalendarView>({ base: 'agenda', md: 'week', xl: 'month' }) || 'month';
  const { events, loading } = useCalendarEvents({ organizationId: orgId || '' });
  const [activeView, setActiveView] = useState<CalendarView>(defaultView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [employees, setEmployees] = useState<Array<{ id: string; label: string }>>([]);
  const [myEmployeeId, setMyEmployeeId] = useState<string>('');
  const [assignee, setAssignee] = useState('all');
  const [scope, setScope] = useState('all');

  React.useEffect(() => {
    const loadPeople = async () => {
      if (!orgId) {
        return;
      }

      const [employeesResponse, meResponse] = await Promise.all([
        getEmployees(orgId),
        getEmployeeDataMe(orgId),
      ]);

      if (employeesResponse?.success && Array.isArray(employeesResponse.data)) {
        setEmployees(
          employeesResponse.data.map((employee) => ({
            id: employee.id,
            label: `${employee.user?.first_name || ''} ${employee.user?.last_name || ''}`.trim() || 'Unknown teammate',
          })),
        );
      }

      if (meResponse?.success && meResponse.data?.id) {
        setMyEmployeeId(meResponse.data.id);
      }
    };

    loadPeople();
  }, [orgId]);

  const filteredEvents = useMemo(() => {
    const query = search.trim().toLowerCase();
    return events.filter((event) => {
      const matchesSearch =
        !query ||
        [event.title, event.description, event.assigned_to_name, event.created_by_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query);
      const matchesType = type === 'all' || event.event_type === type;
      const matchesStatus = status === 'all' || event.status === status;
      const matchesAssignee = assignee === 'all' || event.assigned_to === assignee;
      const matchesScope =
        scope === 'all' ||
        (scope === 'mine' && !!myEmployeeId && event.assigned_to === myEmployeeId) ||
        (scope === 'unassigned' && !event.assigned_to);
      return matchesSearch && matchesType && matchesStatus && matchesAssignee && matchesScope;
    });
  }, [assignee, events, myEmployeeId, scope, search, status, type]);

  const rbcEvents = useMemo(
    () =>
      filteredEvents.map((event) => ({
        id: event.id,
        title: event.title,
        start: new Date(event.start_datetime),
        end: event.end_datetime ? new Date(event.end_datetime) : new Date(event.start_datetime),
        allDay: false,
        resource: event,
      })),
    [filteredEvents],
  );

  const selectedDateEvents = useMemo(
    () =>
      filteredEvents
        .filter((event) => isSameDay(new Date(event.start_datetime), selectedDate))
        .sort(
          (a, b) =>
            new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime(),
        ),
    [filteredEvents, selectedDate],
  );

  const upcomingEvents = useMemo(
    () =>
      filteredEvents
        .filter((event) => event.status === 'upcoming')
        .filter((event) => new Date(event.start_datetime).getTime() >= startOfDay(new Date()).getTime())
        .sort(
          (a, b) =>
            new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime(),
        )
        .slice(0, 5),
    [filteredEvents],
  );

  const eventStyleGetter = (event: { resource: CalendarEvent }) => {
    const meta = getCalendarEventTypeMeta(event.resource.event_type);
    return {
      style: {
        backgroundColor: `${meta.tint}18`,
        border: `1px solid ${meta.tint}33`,
        borderLeft: `3px solid ${meta.tint}`,
        borderRadius: '10px',
        color: '#0F172A',
        padding: '2px 6px',
        boxShadow: 'none',
      },
    };
  };

  const buildCreateEventLink = (start: Date, end?: Date) => {
    const params = new URLSearchParams({
      start: start.toISOString(),
    });
    if (end) {
      params.set('end', end.toISOString());
    }
    return `/organization/${orgId}/events/new?${params.toString()}`;
  };

  const dayPropGetter = (date: Date) => {
    if (isSameDay(date, new Date())) {
      return { style: { backgroundColor: '#F0FDFA' } };
    }
    if (isSameDay(date, selectedDate)) {
      return { style: { backgroundColor: '#F8FAFC' } };
    }
    return {};
  };

  if (loading) {
    return (
      <CalendarShell>
        <CalendarSkeleton />
      </CalendarShell>
    );
  }

  return (
    <CalendarShell>
      <CalendarHero
        eyebrow="Calendar"
        title="Planning, follow-ups, and deadlines in one schedule"
        description="Browse team timing, inspect linked CRM activity, and keep task and lead-driven events visible from one scheduling workspace."
        primaryAction={
          <CalendarPrimaryButton
            leftIcon={<AddIcon />}
            onClick={() => navigate(`/organization/${orgId}/events/new`)}
          >
            New event
          </CalendarPrimaryButton>
        }
        secondaryAction={
          <CalendarSecondaryButton onClick={() => {
            const today = new Date();
            setCurrentDate(today);
            setSelectedDate(today);
          }}>
            Today
          </CalendarSecondaryButton>
        }
      />

      <CalendarCommandBar
        search={search}
        onSearchChange={setSearch}
        type={type}
        onTypeChange={setType}
        status={status}
        onStatusChange={setStatus}
        assignee={assignee}
        onAssigneeChange={setAssignee}
        assignees={employees}
        scope={scope}
        onScopeChange={setScope}
        view={activeView}
        onViewChange={(value) => setActiveView(value as CalendarView)}
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} mt={6} alignItems="start">
        <Box gridColumn={{ xl: 'span 2' }}>
          <CalendarSectionCard
            title={getRangeLabel(currentDate, activeView)}
            description="Use the calendar grid to scan schedule density, then inspect the selected day in the side panel."
            action={
              <HStack spacing={2} flexWrap="wrap">
                <Button variant="outline" borderRadius="14px" size="sm" leftIcon={<ChevronLeftIcon />} onClick={() => {
                  const nextDate = navigateDate(currentDate, activeView, 'prev');
                  setCurrentDate(nextDate);
                  setSelectedDate(nextDate);
                }}>
                  Prev
                </Button>
                <Button variant="outline" borderRadius="14px" size="sm" onClick={() => {
                  const today = new Date();
                  setCurrentDate(today);
                  setSelectedDate(today);
                }}>
                  Today
                </Button>
                <Button variant="outline" borderRadius="14px" size="sm" rightIcon={<ChevronRightIcon />} onClick={() => {
                  const nextDate = navigateDate(currentDate, activeView, 'next');
                  setCurrentDate(nextDate);
                  setSelectedDate(nextDate);
                }}>
                  Next
                </Button>
              </HStack>
            }
          >
            {rbcEvents.length === 0 ? (
              <CalendarEmptyState
                title="No events match this view"
                description="Adjust the filters or move to a different date range to bring scheduled work back into view."
              />
            ) : (
              <Box
                sx={{
                  '.rbc-calendar': { fontFamily: 'inherit' },
                  '.rbc-toolbar': { display: 'none' },
                  '.rbc-header': {
                    padding: '12px 8px',
                    fontWeight: 700,
                    fontSize: '12px',
                    color: '#475569',
                    backgroundColor: '#F8FAFC',
                    borderBottom: '1px solid #E2E8F0',
                  },
                  '.rbc-time-header-content, .rbc-month-view, .rbc-time-view, .rbc-agenda-view': {
                    borderColor: '#E2E8F0',
                  },
                  '.rbc-time-content, .rbc-day-slot .rbc-time-slot, .rbc-timeslot-group, .rbc-month-row + .rbc-month-row': {
                    borderColor: '#E2E8F0',
                  },
                  '.rbc-off-range-bg': { backgroundColor: '#FAFAFA' },
                  '.rbc-today': { backgroundColor: '#ECFDF5' },
                  '.rbc-current-time-indicator': { backgroundColor: calendarAccent },
                  '.rbc-event:focus, .rbc-day-slot .rbc-event:focus': { outline: 'none' },
                  '.rbc-agenda-view table.rbc-agenda-table': { borderColor: '#E2E8F0' },
                  '.rbc-agenda-view table.rbc-agenda-table tbody > tr > td': {
                    padding: '10px 12px',
                    borderColor: '#E2E8F0',
                  },
                }}
              >
                <Calendar
                  localizer={localizer}
                  events={rbcEvents}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: compactView ? 640 : 760 }}
                  view={activeView as any}
                  onView={(view) => setActiveView(view as CalendarView)}
                  date={currentDate}
                  onNavigate={(date) => {
                    setCurrentDate(date);
                    setSelectedDate(date);
                  }}
                  onSelectEvent={(event) => navigate(`/organization/${orgId}/events/${event.resource.id}/view`)}
                  onSelectSlot={(slotInfo) => {
                    setSelectedDate(slotInfo.start);
                    navigate(buildCreateEventLink(slotInfo.start, slotInfo.end));
                  }}
                  selectable
                  popup
                  toolbar={false}
                  eventPropGetter={eventStyleGetter}
                  dayPropGetter={dayPropGetter}
                  components={{ event: CalendarEventContent }}
                />
              </Box>
            )}
          </CalendarSectionCard>
        </Box>

        <Stack spacing={6}>
          <CalendarSectionCard
            title={`Selected day · ${formatCalendarDate(selectedDate.toISOString())}`}
            description="A compact agenda for the day you are inspecting."
            action={<CalendarDays size={18} color="#64748B" />}
          >
            {selectedDateEvents.length === 0 ? (
              <CalendarEmptyState
                title="No events on this day"
                description="Pick another date in the calendar grid or widen the filters to inspect more scheduled work."
              />
            ) : (
              <Stack spacing={3}>
                {selectedDateEvents.map((event) => (
                  <CalendarEventListItem
                    key={event.id}
                    event={event}
                    onClick={() => navigate(`/organization/${orgId}/events/${event.id}/view`)}
                  />
                ))}
              </Stack>
            )}
          </CalendarSectionCard>

          <CalendarSectionCard
            title="Upcoming"
            description="The next scheduled events after now."
            action={<Icon as={ListFilter as ElementType} color="gray.500" boxSize={4.5} />}
          >
            {upcomingEvents.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                No upcoming events were returned for the current filters.
              </Text>
            ) : (
              <Stack spacing={3}>
                {upcomingEvents.map((event) => (
                  <Button
                    key={event.id}
                    variant="ghost"
                    justifyContent="flex-start"
                    h="auto"
                    px={0}
                    py={0}
                    onClick={() => navigate(`/organization/${orgId}/events/${event.id}/view`)}
                    _hover={{ bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}
                  >
                    <HStack
                      align="stretch"
                      spacing={3}
                      w="100%"
                      bg="gray.50"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="18px"
                      px={3.5}
                      py={3}
                    >
                      <Box w="4px" borderRadius="full" bg={getCalendarEventTypeMeta(event.event_type).tint} />
                      <Stack spacing={1} align="flex-start" flex={1}>
                        <Text fontWeight="700" color="gray.900" noOfLines={1}>
                          {event.title}
                        </Text>
                        <HStack spacing={2} flexWrap="wrap">
                          <CalendarTypeBadge type={event.event_type} />
                          <CalendarStatusBadge status={event.status} />
                        </HStack>
                        <Text fontSize="sm" color="gray.500">
                          {formatCalendarDateTime(event.start_datetime)}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {event.assigned_to_name || 'Unassigned'} · {event.related_source === 'task'
                            ? 'Task'
                            : event.related_source === 'lead_followup'
                              ? 'Lead follow-up'
                              : 'Standalone'}
                        </Text>
                      </Stack>
                    </HStack>
                  </Button>
                ))}
              </Stack>
            )}
          </CalendarSectionCard>
        </Stack>
      </SimpleGrid>
    </CalendarShell>
  );
};
